import { useTranslation } from 'react-i18next';
import React from 'react';
import axios from 'axios';
import {
  CircularProgress,
  Collapse,
  FormControlLabel,
  Switch,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedTableCell as TableCell, LocalizedTextField as TextField } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import { formatDisplayDate } from '../../utils/dateDisplay';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const PLANETS = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];
const PLANET_LABELS: Record<string, string> = {
  SUN: 'Sun', MOON: 'Moon', MARS: 'Mars', MERCURY: 'Mercury', JUPITER: 'Jupiter',
  VENUS: 'Venus', SATURN: 'Saturn', RAHU: 'Rahu', KETU: 'Ketu',
};

const ymd = (date: Date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};
const timezoneMinutes = (value: any) => {
  if (Number.isFinite(Number(value))) return Number(value);
  const match = String(value || '').match(/^([+-])(\d{2}):(\d{2})$/);
  return match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3])) : 330;
};

type Props = {
  chartData: any;
  isPremium: boolean;
  token?: string | null;
  onUpgrade: () => void;
};

export default function KpNadiTransitScanner({ chartData, isPremium, token, onUpgrade }: Props) {
  const { t } = useTranslation();
  const today = React.useMemo(() => new Date(), []);
  const defaultEnd = React.useMemo(() => new Date(today.getTime() + 90 * 86400000), [today]);
  const [planets, setPlanets] = React.useState<string[]>(['JUPITER', 'SATURN']);
  const [houses, setHouses] = React.useState<number[]>([2, 6, 10, 11]);
  const [startDate, setStartDate] = React.useState(ymd(today));
  const [endDate, setEndDate] = React.useState(ymd(defaultEnd));
  const [includeStarLord, setIncludeStarLord] = React.useState(false);
  const [includeSubLord, setIncludeSubLord] = React.useState(false);
  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});
  const [time, setTime] = React.useState('12:00');
  const [result, setResult] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  if (!isPremium) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={800}>Nadi Nakshatra Transit Scanner</Typography>
        <Typography color="text.secondary" sx={{ my: 1.5 }}>
          Select strong planets, natal KP cuspal houses and a time period to follow their Nakshatra, Star Lord and Sub Lord movement.
        </Typography>
        <Button variant="contained" color="warning" onClick={onUpgrade}>Unlock for paid users</Button>
      </Paper>
    );
  }

  const togglePlanet = (planet: string) => setPlanets((current) => current.includes(planet) ? current.filter((item) => item !== planet) : [...current, planet]);
  const toggleHouse = (house: number) => setHouses((current) => current.includes(house) ? current.filter((item) => item !== house) : [...current, house].sort((a, b) => a - b));

  const runScan = async () => {
    const cusps = chartData?.houses?.cusps || chartData?.bhavaChalit?.cusps;
    const place = chartData?.birthDetails?.placeOfBirth || {};
    if (!cusps || Array.from({ length: 12 }, (_, index) => cusps[index + 1] || cusps[String(index + 1)]).some((cusp) => !Number.isFinite(Number(cusp?.longitude ?? cusp)))) {
      setError('This chart does not contain all 12 KP cusp longitudes. Recalculate it in KP mode first.');
      return;
    }
    if (!planets.length || !houses.length) {
      setError('Select at least one strong planet and one cuspal house.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setResult(null);
      setExpanded({});
      const response = await axios.post(`${API_BASE_URL}/transits/nadi-nakshatra-scan`, {
        planets, houses, includeStarLord, includeSubLord, startDate, endDate, time, cusps,
        latitude: Number(place.latitude ?? 28.6139),
        longitude: Number(place.longitude ?? 77.2090),
        timezoneOffset: timezoneMinutes(place.timezone ?? place.timezoneOffset ?? 330),
      }, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
      setResult(response.data?.data || null);
    } catch (scanError: any) {
      setError(scanError.response?.data?.message || 'Unable to run the Nadi Nakshatra transit scan.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={800}>Nadi Nakshatra Transit Scanner</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Track selected strong planets only while they move through the chosen natal KP cuspal houses. Maximum period: 366 days.
        </Typography>

        <Typography variant="subtitle2" fontWeight={800}>Strong planets</Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1, mb: 2 }}>
          {PLANETS.map((planet) => <Chip key={planet} label={PLANET_LABELS[planet]} clickable color={planets.includes(planet) ? 'primary' : 'default'} variant={planets.includes(planet) ? 'filled' : 'outlined'} onClick={() => togglePlanet(planet)} />)}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ mb: 1 }}>
          <FormControlLabel control={<Switch checked={includeStarLord} onChange={(_, checked) => setIncludeStarLord(checked)} />} label={t('transit.scanner.includeStarLord', { defaultValue: 'Include star lord' })} />
          <FormControlLabel control={<Switch checked={includeSubLord} onChange={(_, checked) => setIncludeSubLord(checked)} />} label={t('transit.scanner.includeSubLord', { defaultValue: 'Include sub lord' })} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('transit.scanner.lordPositionNote', { defaultValue: "Also scan the current star/sub lord planet's own transit position in the selected natal houses. Dates reflect the daily scan time." })}</Typography>

        <Typography variant="subtitle2" fontWeight={800}>Cuspal houses</Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1, mb: 2 }}>
          {Array.from({ length: 12 }, (_, index) => index + 1).map((house) => <Chip key={house} label={`H${house}`} clickable color={houses.includes(house) ? 'secondary' : 'default'} variant={houses.includes(house) ? 'filled' : 'outlined'} onClick={() => toggleHouse(house)} />)}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField type="date" label="From" value={startDate} onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="date" label="To" value={endDate} onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="time" label="Daily scan time" value={time} onChange={(event) => setTime(event.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={runScan} disabled={loading} sx={{ minWidth: 160 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Scan movements'}
          </Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {result ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>Movement timeline</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {result.housePeriods?.length || result.periods?.length || 0} matching house periods across {result.sampledDays} scanned days. Houses: {result.criteria?.houses?.join(', ')}.
          </Typography>
          {(result.housePeriods || result.periods)?.length ? (
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table stickyHeader size="small">
                <TableHead><TableRow><TableCell>Start date</TableCell><TableCell>End date</TableCell><TableCell>Planet</TableCell><TableCell>Transit by</TableCell><TableCell>House</TableCell><TableCell>Details</TableCell></TableRow></TableHead>
                <TableBody>{(result.housePeriods || result.periods).map((period: any, index: number) => (
                  <React.Fragment key={`${period.planet}-${period.transitBy}-${period.startDate}-${index}`}>
                    <TableRow hover>
                      <TableCell>{formatDisplayDate(period.startDate)}</TableCell><TableCell>{formatDisplayDate(period.endDate)}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{PLANET_LABELS[period.planet] || period.planet}</TableCell>
                      <TableCell>{period.transitBy === 'star' ? `${t('transit.scanner.starLord', { defaultValue: 'Star lord' })} (${PLANET_LABELS[period.sourcePlanet]})` : period.transitBy === 'sub' ? `${t('transit.scanner.subLord', { defaultValue: 'Sub lord' })} (${PLANET_LABELS[period.sourcePlanet]})` : PLANET_LABELS[period.planet]}</TableCell>
                      <TableCell>H{period.house}</TableCell>
                      <TableCell><Button size="small" aria-expanded={!!expanded[index]} onClick={() => setExpanded(current => ({ ...current, [index]: !current[index] }))}>{expanded[index] ? t('transit.scanner.collapse', { defaultValue: 'Collapse' }) : t('transit.scanner.expand', { defaultValue: 'Expand' })}</Button></TableCell>
                    </TableRow>
                    <TableRow><TableCell colSpan={6} sx={{ p: 0, border: 0 }}><Collapse in={!!expanded[index]} unmountOnExit>
                      <Table size="small" aria-label="Star and sub lord details">
                        <TableHead><TableRow><TableCell>Period</TableCell><TableCell>Transit planet</TableCell><TableCell>Sign / Degree</TableCell><TableCell>Nakshatra</TableCell><TableCell>Star Lord</TableCell><TableCell>Sub Lord</TableCell><TableCell>Sub-Sub Lord</TableCell></TableRow></TableHead>
                        <TableBody>{(period.details || [period]).map((detail: any, detailIndex: number) => <TableRow key={detailIndex}>
                          <TableCell>{formatDisplayDate(detail.startDate)} – {formatDisplayDate(detail.endDate)}</TableCell>
                          <TableCell>{PLANET_LABELS[detail.sourcePlanet || detail.planet]}{detail.isRetrograde ? ' R' : ''}</TableCell>
                          <TableCell>{detail.sign} {detail.startDegree} → {detail.endDegree}</TableCell><TableCell>{detail.nakshatra}</TableCell>
                          <TableCell>{detail.starLord}</TableCell><TableCell>{detail.subLord}</TableCell><TableCell>{detail.subSubLord}</TableCell>
                        </TableRow>)}</TableBody>
                      </Table>
                    </Collapse></TableCell></TableRow>
                  </React.Fragment>
                ))}</TableBody>
              </Table>
            </TableContainer>
          ) : <Alert severity="info">No selected planet or included lord entered the chosen cuspal houses during this period.</Alert>}
        </Paper>
      ) : null}
    </Stack>
  );
}
